import type { BrandEmail, EmailSender } from './messagingApi';

/** An email template's limits, as the server's (ADR 0125). */
export const MAX_SUBJECT = 200;
export const MAX_BODY = 20_000;
export const MAX_URL = 500;
export const MAX_FOOTER = 1000;
export const MAX_LINKS = 6;
export const MAX_LINK_LABEL = 40;

/** Moves an item of a list (the channel order). */
export function move<T>(items: readonly T[], from: number, to: number): T[] {
  if (to < 0 || to >= items.length) return [...items];
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item as T);
  return next;
}

/** A link as it may go in an email once filled in: https, http, mailto or tel; null otherwise. */
export function safeLink(url: string): string | null {
  try {
    const parsed = new URL(url.trim());
    return ['https:', 'http:', 'mailto:', 'tel:'].includes(parsed.protocol) ? parsed.href : null;
  } catch {
    return null;
  }
}

/** An image address as it may go in an email: https only. */
export function safeImage(url: string): string | null {
  try {
    const parsed = new URL(url.trim());
    return parsed.protocol === 'https:' ? parsed.href : null;
  } catch {
    return null;
  }
}

/** Fills `{value}` placeholders as the server does: an unknown value becomes empty. */
export const fill = (text: string | null | undefined, values: Readonly<Record<string, string>>) =>
  (text ?? '').replace(/\{([a-z0-9_]+)\}/g, (_, name: string) => values[name] ?? '');

/** An email address a mail server takes, as the server checks it. */
export const isEmail = (text: string) => {
  const at = text.indexOf('@');
  return text.length > 2 && text.length <= 254 && at > 0 && at === text.lastIndexOf('@') && at < text.length - 1 && !/\s/.test(text);
};

export const COLOUR = /^#[0-9a-fA-F]{6}$/;

/**
 * The colours of the email itself, not of HO admin: these mirror the server's renderer (EmailRenderer.cs) so the preview looks like
 * the email customers get. Carbon tokens can't be used here; they would follow HO admin's theme, and an email doesn't.
 */
export const EMAIL_COLOURS = {
  card: '#ffffff',
  ink: '#161616',
  muted: '#525252',
  rule: '#e0e0e0',
  footer: '#f4f4f4',
  buttonText: '#ffffff',
  accent: '#0f62fe',
  background: '#f4f4f4',
} as const;

export type EmailLayout = Pick<BrandEmail, 'logoUrl' | 'accentColor' | 'backgroundColor' | 'footer' | 'links'>;

/** The layout of a brand without settings: the server's plain one. */
export const PLAIN_LAYOUT: EmailLayout = {
  logoUrl: null,
  accentColor: EMAIL_COLOURS.accent,
  backgroundColor: EMAIL_COLOURS.background,
  footer: '',
  links: [],
};

/** A sender's addresses are optional; when typed they must be addresses a mail server takes (the server checks the same). */
export const senderValid = (s: EmailSender) =>
  (!s.fromAddress?.trim() || isEmail(s.fromAddress.trim())) && (!s.replyTo?.trim() || isEmail(s.replyTo.trim())) && (s.fromName ?? '').length <= 100;

/** Empty fields go to the server as null: "use the default". */
export const cleanSender = (s: EmailSender): EmailSender => ({
  accountId: s.accountId || null,
  fromName: s.fromName?.trim() || null,
  fromAddress: s.fromAddress?.trim() || null,
  replyTo: s.replyTo?.trim() || null,
});
